/* import __COLOCATED_TEMPLATE__ from './phone-call-alert.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type TwilioService from 'embercom/services/twilio-service';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import type PhoneNumber from 'embercom/models/inbox/phone-number';
import { tracked } from '@glimmer/tracking';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import type Session from 'embercom/services/session';
import type Calling from 'embercom/models/settings/calling';
import { post } from 'embercom/lib/ajax';

interface Args {}

interface Signature {
  Args: Args;
}

export default class PhoneCallAlertComponent extends Component<Signature> {
  @service declare twilioService: TwilioService;
  @service declare intercomCallService: IntercomCallService;
  @service declare intl: IntlService;
  @service declare session: Session;
  @tracked selectedPhoneNumber: PhoneNumber | undefined;
  @tracked escalationSummary: string | null = null;
  @tracked countdownSeconds = 10;
  @tracked callSettings: Calling | null = null;

  private autoAcceptTimer: number | null = null;
  private countdownTimer: number | null = null;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    let phoneNumbers = this.intercomCallService.workspacePhoneNumbers;
    this.selectedPhoneNumber = phoneNumbers?.firstObject;
    this.fetchEscalationSummary();
    this.loadCallSettings();
  }

  willDestroy() {
    super.willDestroy();
    this.clearAutoAcceptTimer();
    this.clearCountdownTimer();
  }

  private startAutoAcceptTimer() {
    this.clearAutoAcceptTimer();
    this.autoAcceptTimer = window.setTimeout(() => {
      this.acceptCall();
    }, 10000); // 10 seconds
  }

  private startCountdownTimer() {
    this.clearCountdownTimer();
    this.countdownSeconds = 10;

    this.countdownTimer = window.setInterval(() => {
      this.countdownSeconds--;
      if (this.countdownSeconds <= 0) {
        this.clearCountdownTimer();
      }
    }, 1000); // Update every second
  }

  private clearAutoAcceptTimer() {
    if (this.autoAcceptTimer) {
      clearTimeout(this.autoAcceptTimer);
      this.autoAcceptTimer = null;
    }
  }

  private clearCountdownTimer() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  get participants() {
    return this.twilioService.participants;
  }

  get userSummary() {
    return this.twilioService.userSummary;
  }

  get incomingPhoneAlertMessage() {
    if (this.twilioService.isIncomingCallConference) {
      if (this.twilioService.participants.length < 2) {
        return this.intl.t(
          'calling.incoming-phone-call-modal.conference-call-unknown-participants',
        );
      }

      let participantList = this.participants
        .slice(0, -1)
        .map((participant) => participant.displayLabel)
        .join(', ');
      let lastParticipant = this.participants.lastObject?.displayLabel;

      return this.intl.t('calling.incoming-phone-call-modal.conference-call-known-participants', {
        participantList,
        lastParticipant,
      });
    }

    if (!this.userSummary) {
      throw new Error('User summary is not defined');
    } else if (this.isCallerIdBlocked(this.userSummary.phone) === true) {
      return this.intl.t('calling.incoming-phone-call-modal.blocked-number');
    } else if (!this.userSummary.name) {
      if (this.twilioService.isIncomingCallback) {
        return this.intl.t('calling.incoming-phone-call-modal.callback-unknown-number', {
          number: this.userSummary.phone,
        });
      }
      if (
        this.twilioService.isIncomingCallTransfer ||
        this.twilioService.isIncomingCallWarmTransfer
      ) {
        return this.intl.t('calling.incoming-phone-call-modal.transfer-unknown-number', {
          number: this.userSummary.phone,
        });
      }
      return this.intl.t('calling.incoming-phone-call-modal.unknown-number', {
        number: this.userSummary.phone,
      });
    }
    if (
      this.twilioService.isIncomingCallTransfer ||
      this.twilioService.isIncomingCallWarmTransfer
    ) {
      return this.intl.t('calling.incoming-phone-call-modal.transfer-known-number', {
        name: this.userSummary.name,
        number: this.userSummary.phone,
      });
    }

    if (this.twilioService.isIncomingCallback) {
      return this.intl.t('calling.incoming-phone-call-modal.callback-known-number', {
        name: this.userSummary.name,
        number: this.userSummary.phone,
      });
    }
    return this.intl.t('calling.incoming-phone-call-modal.known-number', {
      name: this.userSummary.name,
      number: this.userSummary.phone,
    });
  }

  get currentPhoneNumberTitle() {
    return this.intl.t('calling.modal.current-phone-number', {
      number: this.twilioService.workspacePhoneNumber,
      country: this.twilioService.calledNumberCountryCode,
    });
  }

  get isForceJoinCall() {
    return this.callSettings?.autoAnswerEnabled ?? false;
  }

  get autoJoinCallWarning() {
    if (!this.isForceJoinCall) {
      return null;
    }

    return this.intl.t('calling.incoming-phone-call-modal.warning-on-force-join-call', {
      seconds: this.countdownSeconds,
    });
  }

  isCallerIdBlocked(phone: string | undefined) {
    return phone !== undefined && phone === '+266696687';
  }

  @action
  async acceptCall() {
    await this.twilioService.acceptCall();

    if (
      this.session.workspace.isFeatureEnabled(
        'phone-create-conversation-part-for-teammate-answer-call',
      )
    ) {
      post(`/ember/phone_call/record_accept_inbound_phone_call`, {
        app_id: this.session.workspace.id,
        admin_id: this.session.teammate.id,
        conversation_id: this.twilioService.conversation?.id,
      });
    }
  }

  @action
  async onNumberSelectionChange(option: PhoneNumber) {
    this.selectedPhoneNumber = option;
  }

  @action
  async rejectCall() {
    await this.twilioService.rejectCall();

    if (
      this.session.workspace.isFeatureEnabled(
        'phone-create-conversation-part-for-teammate-decline-call',
      )
    ) {
      post(`/ember/phone_call/record_decline_inbound_phone_call`, {
        app_id: this.session.workspace.id,
        admin_id: this.session.teammate.id,
        conversation_id: this.twilioService.conversation?.id,
      });
    }
  }

  @action
  navigateToConversation() {
    this.twilioService.navigateToConversation();
  }

  @action
  ignoreCallback() {
    this.twilioService.ignoreCallback();
  }

  @action
  async acceptCallback() {
    this.twilioService.acceptCallback();
  }

  async loadCallSettings() {
    try {
      this.callSettings = await this.intercomCallService.getCallSettings();

      // Start auto-accept timer if auto answer is enabled
      if (this.isForceJoinCall) {
        this.startAutoAcceptTimer();
        this.startCountdownTimer();
      }
    } catch (error) {
      console.error('Failed to load call settings:', error);
      // Fallback to false if settings can't be loaded
      this.callSettings = null;
    }
  }

  @action
  async fetchEscalationSummary() {
    let conversationId = this.twilioService.conversation?.id;
    if (!conversationId) {
      return;
    }
    this.escalationSummary = await this.intercomCallService.fetchEscalationSummary(conversationId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::PhoneCallAlert': typeof PhoneCallAlertComponent;
    'inbox2/phone-call-alert': typeof PhoneCallAlertComponent;
  }
}
